import { useState, useMemo, useCallback, useEffect } from "react";
import { isEqual } from "lodash";

import { LegalEntityQuickFilter } from "./interfaces";

import { getLocalStorageValue } from "src/utils/local-storage-utils";
import { CompanyLegalEntitiesApiService } from "src/services/company-legal-entities";
import { CompanyLegalEntityDTO } from "../../../models/company-accounts/company-legal-entities";
import { useAppSelector } from "../../../custom-hooks/redux/redux";
import { isAuthorized } from "src/custom-hooks/useAuthorization/useAuthorization";

export const useLegalEntityQuickFilter = (
  allLegalEntitiesAsDefault = false,
  lsIdentifier?: string,
  authorizations?: string[],
  optionIsAuthorizedChecker?: (...params: any) => boolean,
  optionIncluded?: (le: CompanyLegalEntityDTO) => boolean
) => {
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const [legalEntities, setCurrLegalEntities] = useState<CompanyLegalEntityDTO[]>([]);

  const [quickfilterIsLoading, setQuickfilterIsLoading] = useState(true);

  const loadItemCallback = useCallback(
    async (signal: AbortSignal) => {
      setQuickfilterIsLoading(true);
      try {
        const response = await new CompanyLegalEntitiesApiService().loadItems(
          {
            ids: [],
          },
          undefined,
          undefined, // so that service API default sort is applied
          { pageNumber: 0, pageSize: 1000 },
          undefined,
          60,
          signal
        );
        if (!response.error) {
          const optionsIncluded = !!optionIncluded ? response.items.filter((le) => optionIncluded(le)) : response.items;

          const authorizedLEs = authorizations
            ? optionIsAuthorizedChecker
              ? optionsIncluded.filter((le) =>
                  optionIsAuthorizedChecker([...authorizations], currentUserAuthorizations, [le.id])
                )
              : isAuthorized([...authorizations], currentUserAuthorizations)
              ? optionsIncluded
              : []
            : optionsIncluded;

          setCurrLegalEntities(authorizedLEs);

          setQuickFilter({
            legalEntities:
              optionIncluded && allLegalEntitiesAsDefault
                ? {
                    withoutLegalEntity: false,
                    legalEntityIds: optionsIncluded.map((le) => le.id),
                  }
                : {
                    ...(getLocalStorageValue(lsIdentifier ?? "QF_LE")?.legalEntityIds.length > 0
                      ? {
                          ...getLocalStorageValue(lsIdentifier ?? "QF_LE"),
                        }
                      : {
                          withoutLegalEntity: false,
                          legalEntityIds: allLegalEntitiesAsDefault
                            ? optionIncluded
                              ? optionsIncluded.map((le) => le.id)
                              : []
                            : [authorizedLEs[0]?.id ?? []],
                        }),
                  },
          });
        }
      } catch (error: any) {
        console.log(error);
      } finally {
        setQuickfilterIsLoading(false);
      }
    },
    [authorizations, optionIsAuthorizedChecker, optionIncluded]
  );

  useEffect(() => {
    const abortController = new AbortController();

    loadItemCallback(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, []);

  const [quickFilter, setQuickFilter] = useState<LegalEntityQuickFilter>({
    legalEntities: {
      ...(getLocalStorageValue(lsIdentifier ?? "QF_LE")?.legalEntityIds.length > 0
        ? {
            ...getLocalStorageValue(lsIdentifier ?? "QF_LE"),
          }
        : {
            withoutLegalEntity: false,
            legalEntityIds: allLegalEntitiesAsDefault
              ? optionIncluded
                ? legalEntities.filter((le) => optionIncluded(le)).map((le) => le.id)
                : []
              : [
                  ...(legalEntities[0]?.id
                    ? [legalEntities[0].id]
                    : optionIncluded
                    ? legalEntities.filter((le) => optionIncluded(le)).map((le) => le.id)
                    : []),
                ],
          }),
    },
  });

  const selectedLegalEntityId = useMemo(
    () => quickFilter.legalEntities.legalEntityIds[0],
    [quickFilter.legalEntities.legalEntityIds]
  );

  const selectedLegalEntity = useMemo(
    () => legalEntities.filter((le) => le.id === selectedLegalEntityId)[0] ?? null,
    [legalEntities, selectedLegalEntityId]
  );

  const handleChangeQuickFilter = useCallback(
    (filter: keyof LegalEntityQuickFilter, value: LegalEntityQuickFilter["legalEntities"], allItems?: any) => {
      if (isEqual(value, quickFilter[filter])) return;

      if (!isEqual(allItems, legalEntities) && !!allItems) setCurrLegalEntities(allItems);

      setQuickFilter((state) => ({ ...state, [filter]: value }));

      localStorage.setItem(lsIdentifier ?? "QF_LE", JSON.stringify(value));
    },
    [quickFilter, lsIdentifier]
  );

  return {
    legalEntitiesQuickFilter: quickFilter,
    setLegalEntitiesQuickFilter: handleChangeQuickFilter,
    selectedLegalEntity: selectedLegalEntity,
    legalEntityQFIsLoading: quickfilterIsLoading,
    allLegalEntities: legalEntities,
  };
};
